// markers/registerPdarSprites.ts
import type { Map as MapType } from "maplibre-gl";
import { createPdarMarker, type PdarMarkerType, type Intent } from "./pdar";

const PDAR_MARKER_TYPES: PdarMarkerType[] = ["platform", "target", "sensor"];
const INTENTS: Intent[] = ["friendly", "hostile", "neutral", "unknown"];

export async function registerPdarSprites(map: MapType): Promise<void> {
  const loadPromises: Promise<void>[] = [];

  const registerSpriteImage = (
    spriteKey: string,
    markerType: PdarMarkerType,
    intent: Intent,
    isSelected: boolean,
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      let svgString = createPdarMarker({
        type: markerType,
        intent: intent,
        isSelected: isSelected,
      }).trim();

      if (!svgString.includes("xmlns=")) {
        svgString = svgString.replace(
          "<svg",
          '<svg xmlns="http://www.w3.org/2000/svg"',
        );
      }

      const blob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const img = new Image();

      // HTML5 Image loaders automatically extract and respect the root width/height
      // attributes declared inside the source SVG string payload natively.
      img.onload = () => {
        if (!map.hasImage(spriteKey)) {
          map.addImage(spriteKey, img, { sdf: false });
        }
        URL.revokeObjectURL(url);
        resolve();
      };

      img.onerror = (err) => {
        console.error(
          `[PDAR Sprite Engine Fail] Error building texture array for key: ${spriteKey}`,
          err,
        );
        URL.revokeObjectURL(url);
        resolve();
      };

      img.src = url;
    });
  };

  for (const currentKey of PDAR_MARKER_TYPES) {
    loadPromises.push(
      registerSpriteImage(`pdar-${currentKey}`, currentKey, "neutral", false),
    );

    for (const intent of INTENTS) {
      const combinedSpriteKey = `${intent}-pdar-${currentKey}`;
      const selectedSpriteKey = `${combinedSpriteKey}-selected`;

      loadPromises.push(
        registerSpriteImage(combinedSpriteKey, currentKey, intent, false),
      );

      loadPromises.push(
        registerSpriteImage(selectedSpriteKey, currentKey, intent, true),
      );
    }
  }

  await Promise.all(loadPromises);
  console.log(
    `[PDAR Sprite Engine] Pre-loaded all platform, target, and sensor assets cleanly to the WebGL texture pipeline!`,
  );
}
